/**
 * Cashbook offline backup: collect from Firestore, download ZIP/JSON, overwrite restore.
 */

import {
  Timestamp,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
  type DocumentData
} from 'firebase/firestore';
import { getFirestoreDb } from '@/lib/firebase';
import {
  buildCashbookBackupPayload,
  buildCashbookBackupZipBytes,
  cashbookBackupJsonFilename,
  cashbookBackupZipFilename,
  serializeDocData,
  validateCashbookBackupPayload,
  writeLastJsonBackupAt,
  type CashbookBackupDoc,
  type CashbookBackupPayload
} from './backupPayload';
import {
  cashbookDebtEntriesCol,
  cashbookEntriesCol,
  cashbookSettingsRef,
  invalidateCashbookContextCache
} from './context';
import type { CashbookPermissions } from './mutations';
import { beginCashbookRestore, endCashbookRestore } from './writeGuard';

const FIRESTORE_BATCH_LIMIT = 400;

function assertCanVoid(permissions: CashbookPermissions): void {
  if (!permissions.void) throw new Error('Insufficient permissions');
}

function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function docsFromSnap(
  snap: Awaited<ReturnType<typeof getDocs>>
): CashbookBackupDoc[] {
  return snap.docs.map((d) => ({
    id: d.id,
    data: serializeDocData(d.data() as DocumentData as Record<string, unknown>)
  }));
}

export async function collectCashbookBackup(
  orgId: string,
  permissions: CashbookPermissions
): Promise<CashbookBackupPayload> {
  assertCanVoid(permissions);
  const db = getFirestoreDb();
  const [settingsSnap, entriesSnap, debtSnap] = await Promise.all([
    getDoc(cashbookSettingsRef(db, orgId)),
    getDocs(cashbookEntriesCol(db, orgId)),
    getDocs(cashbookDebtEntriesCol(db, orgId))
  ]);

  return buildCashbookBackupPayload({
    orgId,
    settings: (settingsSnap.data() ?? {}) as Record<string, unknown>,
    entries: docsFromSnap(entriesSnap),
    debtEntries: docsFromSnap(debtSnap)
  });
}

export async function exportCashbookBackupJson(
  orgId: string,
  permissions: CashbookPermissions
): Promise<{ exportedAt: string }> {
  const payload = await collectCashbookBackup(orgId, permissions);
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json;charset=utf-8'
  });
  downloadBlob(cashbookBackupJsonFilename(orgId, payload.exportedAt), blob);
  writeLastJsonBackupAt(orgId, payload.exportedAt);
  return { exportedAt: payload.exportedAt };
}

export async function exportCashbookBackupZip(
  orgId: string,
  permissions: CashbookPermissions
): Promise<{ exportedAt: string }> {
  const payload = await collectCashbookBackup(orgId, permissions);
  const bytes = await buildCashbookBackupZipBytes(payload);
  const blob = new Blob([bytes as BlobPart], { type: 'application/zip' });
  downloadBlob(cashbookBackupZipFilename(payload.exportedAt), blob);
  writeLastJsonBackupAt(orgId, payload.exportedAt);
  return { exportedAt: payload.exportedAt };
}

function reviveTimestampFields(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data };
  for (const key of ['createdAt', 'voidedAt', 'openingSetAt', 'syncVersion']) {
    const v = out[key];
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) out[key] = Timestamp.fromDate(d);
    }
  }
  return out;
}

function restoreEntryWriteData(data: Record<string, unknown>): Record<string, unknown> {
  const revived = reviveTimestampFields(data);
  return {
    ...revived,
    restoredFromBackup: true
  };
}

async function commitBatches(
  ops: Array<(batch: ReturnType<typeof writeBatch>) => void>
): Promise<void> {
  const db = getFirestoreDb();
  for (let i = 0; i < ops.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = ops.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const op of chunk) op(batch);
    await batch.commit();
  }
}

/**
 * Overwrite all cashbook settings + entries + debt entries from a validated backup.
 * Requires void permission. Deletes docs not present in the backup.
 */
export async function restoreCashbookFromBackup(
  orgId: string,
  permissions: CashbookPermissions,
  raw: unknown
): Promise<{ ok: true; entryCount: number; debtCount: number }> {
  assertCanVoid(permissions);
  const validated = validateCashbookBackupPayload(raw, orgId);
  if (!validated.ok) throw new Error(validated.message);

  beginCashbookRestore();
  try {
    const payload = validated.payload;
    const db = getFirestoreDb();

    const [entriesSnap, debtSnap] = await Promise.all([
      getDocs(cashbookEntriesCol(db, orgId)),
      getDocs(cashbookDebtEntriesCol(db, orgId))
    ]);

    const backupEntryIds = new Set(payload.entries.map((e) => e.id));
    const backupDebtIds = new Set(payload.debtEntries.map((e) => e.id));

    const deleteOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
    for (const d of entriesSnap.docs) {
      if (!backupEntryIds.has(d.id)) {
        deleteOps.push((batch) => batch.delete(d.ref));
      }
    }
    for (const d of debtSnap.docs) {
      if (!backupDebtIds.has(d.id)) {
        deleteOps.push((batch) => batch.delete(d.ref));
      }
    }
    await commitBatches(deleteOps);

    const writeOps: Array<(batch: ReturnType<typeof writeBatch>) => void> = [];
    for (const entry of payload.entries) {
      const ref = doc(cashbookEntriesCol(db, orgId), entry.id);
      writeOps.push((batch) => batch.set(ref, restoreEntryWriteData(entry.data)));
    }
    for (const entry of payload.debtEntries) {
      const ref = doc(cashbookDebtEntriesCol(db, orgId), entry.id);
      writeOps.push((batch) => batch.set(ref, restoreEntryWriteData(entry.data)));
    }
    await commitBatches(writeOps);

    const settingsData = reviveTimestampFields({ ...payload.settings });
    delete settingsData.syncVersion;
    await setDoc(
      cashbookSettingsRef(db, orgId),
      {
        ...settingsData,
        syncVersion: serverTimestamp()
      },
      { merge: false }
    );

    invalidateCashbookContextCache(orgId);
    return {
      ok: true,
      entryCount: payload.entries.length,
      debtCount: payload.debtEntries.length
    };
  } finally {
    endCashbookRestore();
  }
}

/** @internal test helper — parse file text */
export function parseCashbookBackupJsonText(text: string, orgId: string) {
  let raw: unknown;
  try {
    raw = JSON.parse(text) as unknown;
  } catch {
    return {
      ok: false as const,
      code: 'invalid_json',
      message: 'Backup file is not valid JSON'
    };
  }
  return validateCashbookBackupPayload(raw, orgId);
}
